import ApiService from '@/services/apiService';
import VDeviceMixin from './mixin';
import { isNullOrEmpty } from '@/utils/vuex';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
export default {
  name: 'CoreDtwinStateVirtualDeviceAggregateEdit',
  mixins: [VDeviceMixin],
  props: {
    // the current edited binding
    lclModel: {
      type: Object,
      required: false,
      default: () => ({})
    },
    twinType: {
      type: String,
      required: false,
      default: null
    },
    tenantId: {
      type: Number,
      required: true
    },
    // the original twin(/virtual-device)-model
    // to be used by some components for additional requesting data
    twinModel: {
      type: Object,
      required: false,
      default: () => ({})
    },
    dataSources: {
      type: Array,
      required: true
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    },
    stateDefinition: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data: function () {
    return {
      loading: false,
      // the current result-set for the autocomplete device search
      virtualDevices: [],
      loadingDevices: false,
      virtualDeviceSearch: '',
      selectedVirtualDevice: null,
      // the retrieved states for the selected device
      virtualDeviceStates: null,
      // the key to find the found array of devices in `virtualDeviceFetch`
      virtualDeviceFetchVariable: 'virtualDevices'
    };
  },
  computed: {
    formIsValid() {
      if (this.$refs['healthStateEdit']) {
        return this.$refs['healthStateEdit'].formIsValid;
      }
      return (this.templateContext || !isNullOrEmpty(this.selectedVirtualDevice)) && !isNullOrEmpty(this.selectedVirtualDeviceState) && this.selectedVirtualDeviceState.length > 0;
    },
    virtualDeviceConfigStates() {
      return get(this.lclModel, 'properties.binding.options.virtualDevice.stateIds');
    },
    stateObjects() {
      return this.virtualDeviceStates;
    },
    virtualDeviceSourceName() {
      return (this.dataSources.find(ds => ds.type === 'virtual-device') || {}).name || this.$i18n_t('DigitalTwin.VirtualDevice.title');
    }
  },
  watch: {
    virtualDeviceSearch(val) {
      if (!val || val.length < 1) {
        // if search value is empty or less than two characters do not start a search
        // but treat the old search as invalid as a new search is as about to happen
        // in this state no results will be shown in the auto-complete dropdown
        // because we set `virtualDevices` to an array with only `selectedVirtualDevice`
        // which will not show up because auto-completes `hide-selected` is true
        // while `loadingDevices` is true or the search is empty or less than two characters
        this.virtualDevices = [this.selectedVirtualDevice].filter(d => d);
        return;
      }

      // do not trigger the device-search (again) after the item has been selected
      // that would clear the variable-set
      if (!this.selectedVirtualDevice || !this.selectedVirtualDevice.title || val !== this.selectedVirtualDevice.title) {
        // set loadingDevices to true as early as the debounced fetchDevices will be called
        // to not delay the loading state when settings it from the debounced method
        this.loadingDevices = true;
        this.fetchDevices();
      }
    },
    selectedVirtualDevice(val) {
      if (val !== null) {
        this.fetchStates();
      }
      this.emitSelection();
    }
  },
  mounted() {
    this.lclModel.properties.binding.options = {
      ...this.lclModel.properties.binding.options,
      virtualDevice: {
        ...this.lclModel.properties.binding.options.virtualDevice
      }
    };
    this.lclModel.properties.binding.options.virtualDevice = {
      ...this.lclModel.properties.binding.options.virtualDevice,
      stateIds: this.lclModel.properties.binding.options.virtualDevice.stateIds || [],
      health: this.lclModel.properties.binding.options.virtualDevice.health || []
    };
    if (this.templateContext) {
      this.fetchStates();
    }
    if (this.mode !== 'create' && this.mode !== 'import' && get(this.lclModel, 'properties.binding.options.virtualDevice.id')) {
      ApiService.virtualDeviceGet(this, get(this.lclModel, 'properties.binding.options.virtualDevice.id'), true).then(res => {
        let device = res;
        if (!device || !device.id) {
          throw new Error('NIOTA_DEVICE_NOT_FOUND');
        }
        this.virtualDevices = device ? [device] : null;
        this.selectedVirtualDevice = device;
      }).catch(error => {
        console.error(get(error, 'response.status'));
        if (get(error, 'response.status') === 403) {
          // user has no permission to read the device
          // probably some other user linked it, and this user cannot read it
          // so emit the no-permission event here to trigger the edit-dialog to be closed
          this.$emit('no-permission', this.$i18n_t('DigitalTwin.DigitalTwin.states.noPermissionError'));
          return;
        }
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        this.virtualDevices = null;
        this.selectedVirtualDevice = null;
      }).finally(() => {
        this.loading = false;
      });
    }
  },
  methods: {
    cloneDeep,
    bridgeBindingChanged(e) {
      if (!isEqual(this.lclModel.properties.binding.options.virtualDevice, e.config) || !isEqual(this.lclModel.value.value, e.value)) {
        this.lclModel.properties.binding.options.virtualDevice = e.config;
        this.lclModel.value.value = e.value;
        this.$emit('bridge-binding-changed', e);
      }
    },
    emitSelection() {
      if (this.$refs['healthStateEdit']) {
        this.aggregateStateValue = this.$refs['healthStateEdit'].emitSelection();
        return;
      }
      const aggregateStateValue = {};
      this.selectedVirtualDeviceState.forEach(aggVar => {
        aggregateStateValue[aggVar.key] = get(aggVar, 'value.value');
        aggregateStateValue[`${aggVar.key}_time`] = get(aggVar, 'value.time');
      });
      this.aggregateStateValue = aggregateStateValue;
      this.$emit('bridge-binding-changed', {
        identifier: get(this.selectedVirtualDevice, 'id'),
        valueType: get(this.selectedVirtualDeviceState, '[0].properties.valueType'),
        variable: get(this.selectedVirtualDeviceState, '[0].key'),
        value: this.aggregateStateValue
      });
    },
    // put the request in separate function, in case some components needs to overwrite it with own logic
    // e.g. in the heat-monitoring module where a custom list of "meters" with custom search-filter needs to be requested
    fetchRequest() {
      return ApiService.virtualDeviceFetch(this, null, null, null, ['bare'], null, 0, 50, [{
        or: [{
          variable: 'title',
          search: this.virtualDeviceSearch
        }, {
          variable: 'id',
          search: this.virtualDeviceSearch
        }]
      }]);
    },
    fetchDevices: debounce(function () {
      this.loadingDevices = true;
      this.fetchRequest().then(res => {
        this.virtualDevices = get(res, this.virtualDeviceFetchVariable) || [];
      }).catch(error => {
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        // logException(error);
        this.virtualDevices = [];
      }).finally(() => {
        this.loadingDevices = false;
      });
    }, 250),
    fetchStates() {
      this.virtualDeviceStates = null;
      if (this.templateContext) {
        this.virtualDeviceStates = this.stateDefinition || [];
        return;
      }
      if (!get(this, 'selectedVirtualDevice.id')) {
        return;
      }
      ApiService.digitalTwinFetchStates(this, this.selectedVirtualDevice.id).then(states => {
        this.virtualDeviceStates = states.map(this.mapStateToUi);
        this.selectedVirtualDeviceState = cloneDeep(this.virtualDeviceStates.map(s => {
          if (this.selectedVirtualDeviceState.findIndex(s1 => s1.key === s.key) >= 0) {
            return s;
          }
          return null;
        }).filter(s => s));
      }).catch(error => {
        console.error(error);
        let errorMessage = get(error, 'response.data.message', error.message);
        let errorOutput = this.$i18n_te(`Error.${errorMessage}`) ? this.$i18n_t(`Error.${errorMessage}`) : this.$i18n_t(errorMessage);
        this.$snack(errorOutput, 'error');
        // logException(error);

        this.virtualDeviceStates = [];
      });
    },
    saveState() {
      if (this.$refs['healthStateEdit']) {
        this.$refs['healthStateEdit'].saveState();
        return;
      }
      this.lclModel.properties.binding.options.virtualDevice = {
        id: get(this.selectedVirtualDevice, 'id'),
        stateIds: (this.selectedVirtualDeviceState || []).map(s => s.id || s.key)
      };
    }
  }
};